import { safeGetItem, safeSetItem } from '../lib/safeStorage';
import type { BagItemDto } from '../api/bag';

// SCREENS.md /bag "offline": "Read-only cached bag + reconnecting banner; actions disabled."
// task-16-brief.md: "the §3 'offline inventory view', localStorage-based; SW untouched — ruling
// logged" — the service worker's own precache/runtime-cache strategy (web/src/sw.ts) is
// unmodified by this feature; this is a SEPARATE, purpose-built snapshot of the bag's own
// contents only, read back only when BOTH offline AND the live `useBag()` query has no cached
// react-query data of its own to fall back on (a cold app open while offline, or a query that's
// been garbage-collected out of the cache).
const SNAPSHOT_KEY = 'pp.bag.snapshot';

/** Called on every successful `useBag()` fetch (BagScreen's own effect) — never gated on
 *  anything else, so the snapshot is always as fresh as the last successful online fetch. */
export function saveBagSnapshot(items: BagItemDto[]): void {
  try {
    safeSetItem(SNAPSHOT_KEY, JSON.stringify(items));
  } catch {
    // JSON.stringify on a plain DTO array cannot realistically throw — defensive only, mirrors
    // readBagSnapshot's own try/catch below rather than leaving one guarded and the other not.
  }
}

/** Returns the last successfully saved bag snapshot, or `null` if none exists yet (a fresh
 *  install that has never been online) or the stored value is corrupt/unparsable. Never throws —
 *  `safeGetItem` already swallows storage-access failures; this additionally guards the
 *  `JSON.parse` against a hand-edited or truncated value. */
export function readBagSnapshot(): BagItemDto[] | null {
  const raw = safeGetItem(SNAPSHOT_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as BagItemDto[]) : null;
  } catch {
    return null;
  }
}

/**
 * Item arrival beat (task-7-brief.md, docs/DESIGN.md §21). Codes granted since the Bag last
 * consumed them, written by a grant mutation's `onSuccess` (usePurchase/useForage/useClaimFreebie)
 * and read by BagScreen. Module-level (not a query-cache entry): it is a transient UI cue about
 * *this session's* own actions, never itself data worth caching/hydrating.
 *
 * ## Nothing here may be consumed during render (2026-09-05, e2e smoke)
 *
 * This module used to expose a `takeAcquired()` that CLEARED as it read, called straight out of
 * `BagScreen`'s `useMemo` — i.e. it mutated shared module state *during render* — plus a
 * `deferAcquired()` that put a missed code back for exactly one more list. That pair was written
 * for I4 (the stale-cache path, see `BagScreen`'s own comment) and it **did not work in a real
 * browser**, for a reason no unit test in this repo could see: none of them rendered the screen
 * inside `<StrictMode>`, and the real app (`main.tsx`) is wrapped in one.
 *
 * Measured on React 18.3.1, not inferred: `<StrictMode>`'s mount double-invoke re-runs the
 * component body AND re-mounts its hooks — `useMemo`'s calculator runs a second time (a dep array
 * cannot suppress it; there is no first-invoke memo to hit) and `useRef` hands back a **different
 * object**, so the `arrivalRef` guard that was supposed to make the drain idempotent was itself
 * reset. So on the stale list: invoke 1 drained the code, missed, and spent its one put-back;
 * invoke 2 — a fresh ref, so no guard — drained that put-back, missed again, and `deferAcquired`
 * discarded the code for good. The fresh list a moment later drained an empty set. The whole
 * one-extra-list budget was spent inside a single render of a single list.
 *
 * The general rule this cost us: **a render can be started, discarded and re-run (StrictMode's
 * double-invoke, or a concurrent render React throws away), and any mutation it performed is not
 * rolled back.** So render only ever *reads* (`peekAcquired`), and every mutation happens in an
 * effect — which runs only on committed renders — and is written to be idempotent under
 * `<StrictMode>`'s double-invoked effects as well.
 */
let acquired = new Set<string>();
/** code → the identity of the list it was last seen ABSENT from. This is what caps the wait at one
 *  extra list without a counter: a counter cannot tell a second *list* from a second *invocation of
 *  the same render/effect*, which is exactly the distinction the bug above turned on. */
const missedAgainst = new Map<string, unknown>();

export function markAcquired(codes: string[]): void {
  for (const c of codes) {
    acquired.add(c);
    // A genuinely new grant of the same code starts over: it gets its own miss budget, rather than
    // inheriting an earlier grant's spent one.
    missedAgainst.delete(c);
  }
}

/** PURE read — the only one of these that render may call. Returns the live set (never a copy) so
 *  a render costs nothing; callers must not mutate it. */
export function peekAcquired(): ReadonlySet<string> {
  return acquired;
}

/**
 * Consume codes the Bag has actually drawn. **Effect-only** — never call this during render.
 * Idempotent: consuming a code twice (a `<StrictMode>` double-invoked effect) is a no-op.
 */
export function consumeAcquired(codes: Iterable<string>): void {
  for (const c of codes) {
    acquired.delete(c);
    missedAgainst.delete(c);
  }
}

/**
 * Record that `codes` were absent from the list identified by `listKey` (BagScreen passes the
 * `BagDto` reference react-query handed it, which changes on exactly the events that matter —
 * structural sharing means a refetch returning identical content keeps the same reference).
 * **Effect-only**, same as `consumeAcquired`.
 *
 * A code that misses two DIFFERENT lists is dropped. That is the same "exactly one extra list"
 * budget the old `deferAcquired` intended, but keyed to the list rather than counted per call, so
 * it is **idempotent**: being told about the same miss again — a `<StrictMode>` double-invoked
 * effect, a re-run after a discarded render — costs nothing.
 *
 * Why the budget exists at all: a grant whose item never appears (consumed, gifted or sold before
 * the Bag was ever opened) would otherwise sit here for the life of the page and fire a beat on
 * some unrelated future load. Why the budget is one extra list rather than zero: the stale-cache
 * sequence has exactly two lists in it — the stale one an invalidation left behind, and the fresh
 * one it scheduled. Neither `isStale` (true of everything here; the `QueryClient` runs at the
 * default `staleTime: 0`) nor `isFetching` (false on the very render between the cached list being
 * returned and the refetch starting) can answer "could this list still be superseded?"; this does
 * not need to.
 */
export function noteAcquiredMiss(codes: Iterable<string>, listKey: unknown): void {
  for (const c of codes) {
    if (!acquired.has(c)) continue;
    const prev = missedAgainst.get(c);
    if (prev === undefined) {
      missedAgainst.set(c, listKey);
      continue;
    }
    if (prev === listKey) continue; // the same list again — not a second chance spent
    acquired.delete(c);
    missedAgainst.delete(c);
  }
}

/** Test support only — the production app has no reason to forget a pending grant. Mirrors
 *  `useFlyerQueue.reset()`/`useCelebrationQueue.reset()`, which exist for the same reason: these
 *  cues are deliberately module-level (they must outlive the screen that draws them), so they also
 *  outlive a test unless a suite clears them. */
export function resetAcquired(): void {
  acquired = new Set();
  missedAgainst.clear();
}
